import {ProgressiveLoadingStatus, type ProgressiveDiffEntry} from '../types/progressive-diff-types'

/*
 * Given a list of diff entries and an entry to start from, this function
 * identifies the next entries to load.
 *
 * If the starting entry is marked to load solo, it will return that entry
 * only. Otherwise, it will return the next four entries that aren't loaded
 * yet, starting from the given entry.
 */
export function identifyDiffEntriesToLoad({
  progressiveDiffEntries,
  startingAt,
  preferAnySoloEntry,
}: {
  progressiveDiffEntries: ProgressiveDiffEntry[]
  startingAt: ProgressiveDiffEntry
  preferAnySoloEntry?: boolean
}): ProgressiveDiffEntry[] {
  // This shouldn't be necessary, because there is logic later in this
  // function to handle solo entries. It is conditional to allow callers to
  // pass the value of a feature flag.
  if (preferAnySoloEntry) {
    const selectedEntryToLoadFirst = progressiveDiffEntries.find(
      e => e.loadSolo && e.loadingStatus === ProgressiveLoadingStatus.NotLoaded,
    )
    if (selectedEntryToLoadFirst) {
      return [selectedEntryToLoadFirst]
    }
  }

  const startingAtIndex = progressiveDiffEntries.findIndex(entry => entry.pathDigest === startingAt.pathDigest)
  if (startingAtIndex === -1) {
    return []
  }

  const startingEntry = progressiveDiffEntries[startingAtIndex]
  if (startingEntry && startingEntry.loadSolo && startingEntry.loadingStatus === ProgressiveLoadingStatus.NotLoaded) {
    return [startingEntry]
  }

  const result: ProgressiveDiffEntry[] = []
  for (let i = startingAtIndex; i < progressiveDiffEntries.length; i++) {
    const entry = progressiveDiffEntries[i]

    if (!entry) break

    if (entry.loadingStatus === ProgressiveLoadingStatus.NotLoaded) {
      result.push(entry)
    }

    // TODO: More nuanced logic for when to stop loading diffs,
    // e.g. based on number of changed lines
    if (result.length >= 4) {
      break
    }
  }

  return result
}
